import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Package, Search, Filter, LogOut, Calendar, Mail, User, Box, ShoppingCart, Settings, Trash2, Copy, Ticket, Tag, Bot, BarChart3, Activity, Users, Sparkles, Tv, ChefHat, UserPlus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';
import { toast } from 'sonner';
import { ProductManagement } from './ProductManagement';
import { BoxSizeManagement } from './BoxSizeManagement';
import { PromoCodeManagement } from './PromoCodeManagement';
import { AnalyticsDashboard } from './AnalyticsDashboard';
import { TedAIScans } from './TedAIScans';
import { TikTokLiveAdmin } from './TikTokLiveAdmin';
import { LiveMatchScreen } from './LiveMatchScreen';
import { ServiceStatus } from './ServiceStatus';
import { CustomerManagement } from './CustomerManagement';
import { TedsTableAdmin } from './TedsTableAdmin';
import { TedsWheelAdmin } from '../components/TedsWheelAdmin';
import { NewSignupsAdmin } from '../components/NewSignupsAdmin';
import { ActivityEventsAdmin } from '../components/ActivityEventsAdmin';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminDashboard = () => {
  useDocumentTitle('Admin Dashboard — Ultimate Pet Foods', 'Admin control panel for Ultimate Pet Foods.');
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('orders');
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(localStorage.getItem('admin_search') || '');
  const [boxSizeFilter, setBoxSizeFilter] = useState(localStorage.getItem('admin_box_filter') || 'all');
  const [processedFilter, setProcessedFilter] = useState(localStorage.getItem('admin_status_filter') || 'all');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [tedAiEnabled, setTedAiEnabled] = useState(true);
  const [priceComparisonEnabled, setPriceComparisonEnabled] = useState(true);

  useEffect(() => {
    fetchOrders();
    fetchStats();
    fetchTedAiSetting();
    fetchPriceComparisonSetting();
    
    // Auto-refresh stats every 10 seconds for live active users count
    const statsInterval = setInterval(fetchStats, 10000);
    
    // Auto-refresh orders every 15 seconds to catch new orders
    const ordersInterval = setInterval(fetchOrders, 15000);
    
    return () => {
      clearInterval(statsInterval);
      clearInterval(ordersInterval);
    };
  }, []);

  useEffect(() => {
    filterOrders();
  }, [orders, searchTerm, boxSizeFilter, processedFilter]);

  const getAuthHeader = () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  };

  const fetchOrders = async () => {
    try {
      const headers = getAuthHeader();
      if (!headers) return;

      const response = await axios.get(`${API}/admin/orders`, { headers });
      const newOrders = response.data;
      
      // Check if there are new orders
      if (orders.length > 0 && newOrders.length > orders.length) {
        const newOrderCount = newOrders.length - orders.length;
        toast.success(`${newOrderCount} new order${newOrderCount > 1 ? 's' : ''} received!`, {
          duration: 5000,
        });
      }
      
      setOrders(newOrders);
    } catch (error) {
      console.error('Error fetching orders:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Session expired. Please login again.');
        handleLogout();
      } else {
        toast.error('Failed to load orders');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const headers = getAuthHeader();
      if (!headers) return;

      const response = await axios.get(`${API}/admin/stats`, { headers });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchTedAiSetting = async () => {
    try {
      const response = await axios.get(`${API}/settings/ted-ai-enabled`);
      setTedAiEnabled(response.data.enabled);
    } catch (error) {
      console.error('Error fetching Ted AI setting:', error);
    }
  };

  const toggleTedAi = async () => {
    try {
      const headers = getAuthHeader();
      if (!headers) return;
      const response = await axios.put(`${API}/admin/settings/ted-ai-enabled`, {}, { headers });
      setTedAiEnabled(response.data.enabled);
      toast.success(`Ted AI ${response.data.enabled ? 'enabled' : 'disabled'} for customers`);
    } catch (error) {
      toast.error('Failed to update Ted AI setting');
    }
  };

  const fetchPriceComparisonSetting = async () => {
    try {
      const response = await axios.get(`${API}/settings/price-comparison-enabled`);
      setPriceComparisonEnabled(response.data.enabled);
    } catch (error) {
      console.error('Error fetching price comparison setting:', error);
    }
  };

  const togglePriceComparison = async () => {
    try {
      const headers = getAuthHeader();
      if (!headers) return;
      const response = await axios.put(`${API}/admin/settings/price-comparison-enabled`, {}, { headers });
      setPriceComparisonEnabled(response.data.enabled);
      toast.success(`Price comparison ${response.data.enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to update price comparison setting');
    }
  };

  const filterOrders = () => {
    let filtered = orders;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (order) =>
          order.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          order.customer_email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Box size filter
    if (boxSizeFilter !== 'all') {
      filtered = filtered.filter((order) => order.box_size === parseInt(boxSizeFilter));
    }

    // Processed status filter
    if (processedFilter === 'processed') {
      filtered = filtered.filter((order) => order.processed === true);
    } else if (processedFilter === 'not-processed') {
      filtered = filtered.filter((order) => order.processed !== true);
    }

    setFilteredOrders(filtered);
  };

  const handleToggleProcessed = async (orderId, currentStatus) => {
    try {
      const headers = getAuthHeader();
      if (!headers) return;

      const newStatus = !currentStatus;
      
      // Send as request body, not query parameter
      await axios.patch(
        `${API}/admin/orders/${orderId}`,
        { processed: newStatus },
        { 
          headers,
          params: { processed: newStatus }
        }
      );

      // Update local state
      setOrders((prevOrders) =>
        prevOrders.map((order) =>
          order.id === orderId ? { ...order, processed: newStatus } : order
        )
      );

      toast.success(newStatus ? 'Order marked as processed' : 'Order marked as not processed');
    } catch (error) {
      console.error('Error updating order:', error);
      toast.error('Failed to update order status');
    }
  };

  const handleDeleteOrder = async (orderId) => {
    try {
      const headers = getAuthHeader();
      if (!headers) return;

      await axios.delete(`${API}/admin/orders/${orderId}`, { headers });

      // Remove from local state
      setOrders((prevOrders) => prevOrders.filter((order) => order.id !== orderId));
      setDeleteConfirm(null);
      fetchStats(); // Refresh stats after deletion
      
      toast.success('Order deleted permanently');
    } catch (error) {
      console.error('Error deleting order:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete order');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    // Keep filter preferences on logout
    navigate('/admin/login');
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    localStorage.setItem('admin_search', value);
  };

  const handleBoxSizeChange = (value) => {
    setBoxSizeFilter(value);
    localStorage.setItem('admin_box_filter', value);
  };

  const handleStatusChange = (value) => {
    setProcessedFilter(value);
    localStorage.setItem('admin_status_filter', value);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getBoxName = (size) => {
    const boxes = {
      3: 'Starter Box',
      5: 'Discovery Box',
      10: 'Ultimate Box',
    };
    return boxes[size] || `${size} Samples`;
  };

  const getBoxPrice = (size) => {
    const prices = {
      3: 2.99,
      5: 3.99,
      10: 9.99,
    };
    return prices[size] || 0;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#0FA49C] mx-auto mb-4"></div>
          <p className="text-[#666]">Loading orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#0FA49C] rounded-lg flex items-center justify-center">
                <Package className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-[#222]">Admin Dashboard</h1>
                <p className="text-sm text-[#666] flex items-center gap-1">
                  Ultimate Pet Foods
                  {activeTab === 'orders' && (
                    <span className="inline-flex items-center gap-1 ml-2">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                      </span>
                      <span className="text-xs text-green-600">Auto-refresh: 15s</span>
                    </span>
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Ted AI Toggle */}
              <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border" data-testid="ted-ai-toggle-container">
                <Bot className="w-4 h-4 text-[#666]" />
                <span className="text-xs font-medium text-[#666]">Ted AI</span>
                <button
                  onClick={toggleTedAi}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    tedAiEnabled ? 'bg-[#0FA49C]' : 'bg-gray-300'
                  }`}
                  data-testid="ted-ai-toggle"
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm ${
                      tedAiEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              {/* Price Comparison Toggle */}
              <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border" data-testid="price-comparison-toggle-container">
                <Search className="w-4 h-4 text-[#666]" />
                <span className="text-xs font-medium text-[#666]">Price Search</span>
                <button
                  onClick={togglePriceComparison}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    priceComparisonEnabled ? 'bg-[#0FA49C]' : 'bg-gray-300'
                  }`}
                  data-testid="price-comparison-toggle"
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm ${
                      priceComparisonEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              <Button
                onClick={handleLogout}
                variant="outline"
                className="flex items-center gap-2"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </Button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mt-4 border-b -mb-px">
            <button
              onClick={() => setActiveTab('orders')}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'orders'
                  ? 'border-[#0FA49C] text-[#0FA49C]'
                  : 'border-transparent text-[#666] hover:text-[#222]'
              }`}
              data-testid="orders-tab"
            >
              <ShoppingCart className="w-4 h-4" />
              Orders
            </button>
            <button
              onClick={() => setActiveTab('products')}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'products'
                  ? 'border-[#0FA49C] text-[#0FA49C]'
                  : 'border-transparent text-[#666] hover:text-[#222]'
              }`}
              data-testid="products-tab"
            >
              <Settings className="w-4 h-4" />
              Products
            </button>
            <button
              onClick={() => setActiveTab('box-sizes')}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'box-sizes'
                  ? 'border-[#0FA49C] text-[#0FA49C]'
                  : 'border-transparent text-[#666] hover:text-[#222]'
              }`}
              data-testid="box-sizes-tab"
            >
              <Box className="w-4 h-4" />
              Box Sizes
            </button>
            <button
              onClick={() => setActiveTab('promo-codes')}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'promo-codes'
                  ? 'border-[#0FA49C] text-[#0FA49C]'
                  : 'border-transparent text-[#666] hover:text-[#222]'
              }`}
              data-testid="promo-codes-tab"
            >
              <Tag className="w-4 h-4" />
              Promo Codes
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'analytics'
                  ? 'border-[#0FA49C] text-[#0FA49C]'
                  : 'border-transparent text-[#666] hover:text-[#222]'
              }`}
              data-testid="analytics-tab"
            >
              <BarChart3 className="w-4 h-4" />
              Analytics
            </button>
            <button
              onClick={() => setActiveTab('ted-ai-scans')}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'ted-ai-scans'
                  ? 'border-[#0FA49C] text-[#0FA49C]'
                  : 'border-transparent text-[#666] hover:text-[#222]'
              }`}
              data-testid="ted-ai-scans-tab"
            >
              <Bot className="w-4 h-4" />
              Ted AI Scans
            </button>
            <button
              onClick={() => setActiveTab('tiktok-live')}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'tiktok-live'
                  ? 'border-[#0FA49C] text-[#0FA49C]'
                  : 'border-transparent text-[#666] hover:text-[#222]'
              }`}
              data-testid="tiktok-live-tab"
            >
              <Sparkles className="w-4 h-4" />
              TikTok Live
            </button>
            <button
              onClick={() => setActiveTab('live-show')}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'live-show'
                  ? 'border-[#0FA49C] text-[#0FA49C]'
                  : 'border-transparent text-[#666] hover:text-[#222]'
              }`}
              data-testid="live-show-tab"
            >
              <Tv className="w-4 h-4" />
              Live Show
            </button>
            <button
              onClick={() => setActiveTab('teds-table')}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'teds-table'
                  ? 'border-[#0FA49C] text-[#0FA49C]'
                  : 'border-transparent text-[#666] hover:text-[#222]'
              }`}
              data-testid="teds-table-tab"
            >
              <ChefHat className="w-4 h-4" />
              Ted&apos;s Table
            </button>
            <button
              onClick={() => setActiveTab('new-signups')}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'new-signups'
                  ? 'border-[#0FA49C] text-[#0FA49C]'
                  : 'border-transparent text-[#666] hover:text-[#222]'
              }`}
              data-testid="new-signups-tab"
            >
              <UserPlus className="w-4 h-4" />
              New Signups
            </button>
            <button
              onClick={() => setActiveTab('activities')}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'activities'
                  ? 'border-[#0FA49C] text-[#0FA49C]'
                  : 'border-transparent text-[#666] hover:text-[#222]'
              }`}
              data-testid="activities-tab"
            >
              <Calendar className="w-4 h-4" />
              Activities
            </button>
            <button
              onClick={() => setActiveTab('customers')}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'customers'
                  ? 'border-[#0FA49C] text-[#0FA49C]'
                  : 'border-transparent text-[#666] hover:text-[#222]'
              }`}
              data-testid="customers-tab"
            >
              <Users className="w-4 h-4" />
              Customers
            </button>
            <button
              onClick={() => setActiveTab('teds-wheel')}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'teds-wheel'
                  ? 'border-[#0FA49C] text-[#0FA49C]'
                  : 'border-transparent text-[#666] hover:text-[#222]'
              }`}
              data-testid="teds-wheel-tab"
            >
              <Sparkles className="w-4 h-4" />
              Ted&apos;s Wheel
            </button>
            <button
              onClick={() => setActiveTab('services')}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'services'
                  ? 'border-[#0FA49C] text-[#0FA49C]'
                  : 'border-transparent text-[#666] hover:text-[#222]'
              }`}
              data-testid="services-tab"
            >
              <Activity className="w-4 h-4" />
              Services
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'products' ? (
          <ProductManagement />
        ) : activeTab === 'box-sizes' ? (
          <BoxSizeManagement />
        ) : activeTab === 'promo-codes' ? (
          <PromoCodeManagement />
        ) : activeTab === 'analytics' ? (
          <AnalyticsDashboard />
        ) : activeTab === 'ted-ai-scans' ? (
          <TedAIScans />
        ) : activeTab === 'tiktok-live' ? (
          <TikTokLiveAdmin />
        ) : activeTab === 'live-show' ? (
          <LiveMatchScreen />
        ) : activeTab === 'teds-table' ? (
          <TedsTableAdmin />
        ) : activeTab === 'teds-wheel' ? (
          <TedsWheelAdmin />
        ) : activeTab === 'new-signups' ? (
          <NewSignupsAdmin />
        ) : activeTab === 'activities' ? (
          <ActivityEventsAdmin />
        ) : activeTab === 'customers' ? (
          <CustomerManagement />
        ) : activeTab === 'services' ? (
          <ServiceStatus />
        ) : (
          <>
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-4 shadow-sm border border-orange-400"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-xs text-white/90 mb-1">To Process</p>
                  <p className="text-2xl font-bold text-white">{stats.pending_orders || 0}</p>
                </div>
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Package className="w-5 h-5 text-white" />
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl p-4 shadow-sm border"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-xs text-[#666] mb-1">Total Orders</p>
                  <p className="text-2xl font-bold text-[#222]">{stats.total_orders}</p>
                </div>
                <div className="w-10 h-10 bg-[#0FA49C]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Package className="w-5 h-5 text-[#0FA49C]" />
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 shadow-sm border border-green-400"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-xs text-white/90 mb-1">Live Users</p>
                  <p className="text-2xl font-bold text-white">{stats.active_users || 0}</p>
                  <div className="flex items-center gap-1 mt-1">
                    <div className="w-1.5 h-1.5 bg-green-300 rounded-full animate-pulse"></div>
                    <span className="text-[10px] text-white/75">Live</span>
                  </div>
                </div>
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <User className="w-5 h-5 text-white" />
                </div>
              </div>
            </motion.div>

            {[3, 5, 10].map((size, index) => (
              <motion.div
                key={size}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: (index + 3) * 0.1 }}
                className="bg-white rounded-xl p-4 shadow-sm border"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-xs text-[#666] mb-1 truncate">{getBoxName(size)}</p>
                    <p className="text-2xl font-bold text-[#222]">
                      {stats.box_size_breakdown[size] || 0}
                    </p>
                  </div>
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Box className="w-5 h-5 text-blue-600" />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 shadow-sm border mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium text-[#666] mb-2 block">
                Search by Name or Email
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#666]" />
                <Input
                  type="text"
                  placeholder="Search customers..."
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-[#666] mb-2 block">
                Filter by Box Size
              </label>
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#666]" />
                <select
                  value={boxSizeFilter}
                  onChange={(e) => handleBoxSizeChange(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0FA49C] focus:border-transparent"
                >
                  <option value="all">All Box Sizes</option>
                  <option value="3">Starter Box (3 samples)</option>
                  <option value="5">Discovery Box (5 samples)</option>
                  <option value="10">Ultimate Box (10 samples)</option>
                </select>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-[#666] mb-2 block">
                Filter by Status
              </label>
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#666]" />
                <select
                  value={processedFilter}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0FA49C] focus:border-transparent"
                >
                  <option value="all">All Orders</option>
                  <option value="processed">Processed</option>
                  <option value="not-processed">Not Processed</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Orders Table */}
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#666] uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#666] uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#666] uppercase tracking-wider">
                    Box Size
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#666] uppercase tracking-wider">
                    Products (SKU)
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#666] uppercase tracking-wider">
                    Discount Code
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#666] uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-[#666] uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredOrders.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center text-[#666]">
                      {searchTerm || boxSizeFilter !== 'all' || processedFilter !== 'all'
                        ? 'No orders match your filters'
                        : 'No orders yet'}
                    </td>
                  </tr>
                ) : (
                  filteredOrders.map((order) => (
                    <tr key={order.id} className={`hover:bg-gray-50 ${order.processed ? 'bg-green-50/30' : ''}`}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={order.processed || false}
                            onChange={() => handleToggleProcessed(order.id, order.processed)}
                            className="w-5 h-5 text-[#0FA49C] border-gray-300 rounded focus:ring-[#0FA49C] cursor-pointer"
                          />
                          <span className="text-sm text-[#666]">
                            {order.processed ? 'Processed' : 'Pending'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-start gap-2">
                          <User className="w-4 h-4 text-[#666] mt-1 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-[#222]">{order.customer_name}</p>
                            <div className="flex items-center gap-1 text-sm text-[#666]">
                              <Mail className="w-3 h-3" />
                              {order.customer_email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-[#0FA49C]/10 text-[#0FA49C]">
                            {getBoxName(order.box_size)}
                          </span>
                          <p className="text-sm text-[#666] mt-1">
                            £{getBoxPrice(order.box_size).toFixed(2)}
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {order.selected_products.map((sku) => (
                            <span
                              key={sku}
                              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-[#666]"
                            >
                              {sku}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {order.discount_code ? (
                          <div className="flex items-center gap-2">
                            <div className="flex items-center gap-1 px-2 py-1 bg-amber-50 border border-amber-200 rounded-lg">
                              <Ticket className="w-3 h-3 text-amber-600" />
                              <span className="font-mono text-sm font-medium text-amber-700">
                                {order.discount_code}
                              </span>
                            </div>
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(order.discount_code);
                                toast.success('Discount code copied!');
                              }}
                              className="p-1 hover:bg-gray-100 rounded text-[#666] hover:text-[#0FA49C] transition-colors"
                              title="Copy code"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <span className="text-sm text-[#999]">—</span>
                        )}
                        {order.promo_code_used && (
                          <div className="mt-1">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-50 border border-green-200 rounded text-xs text-green-700 font-medium">
                              <Tag className="w-3 h-3" />
                              Promo: {order.promo_code_used}
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1 text-sm text-[#666]">
                          <Calendar className="w-4 h-4" />
                          {formatDate(order.created_at)}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        {order.processed && (
                          <button
                            onClick={() => setDeleteConfirm(order)}
                            className="p-2 hover:bg-red-50 rounded-lg text-[#666] hover:text-red-600 transition-colors"
                            title="Delete order"
                            data-testid={`delete-order-${order.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Results Count */}
        <div className="mt-4 text-sm text-[#666] text-center">
          Showing {filteredOrders.length} of {orders.length} orders
        </div>

        {/* Delete Order Confirmation Modal */}
        <AnimatePresence>
          {deleteConfirm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
              onClick={() => setDeleteConfirm(null)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                    <Trash2 className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#222]">Delete Order?</h3>
                    <p className="text-sm text-[#666]">This action cannot be undone.</p>
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <p className="text-sm text-[#666]">
                    <strong>Customer:</strong> {deleteConfirm.customer_name}
                  </p>
                  <p className="text-sm text-[#666]">
                    <strong>Email:</strong> {deleteConfirm.customer_email}
                  </p>
                  <p className="text-sm text-[#666]">
                    <strong>Box:</strong> {getBoxName(deleteConfirm.box_size)} ({deleteConfirm.selected_products.length} items)
                  </p>
                  <p className="text-sm text-[#666]">
                    <strong>Date:</strong> {formatDate(deleteConfirm.created_at)}
                  </p>
                </div>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setDeleteConfirm(null)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => handleDeleteOrder(deleteConfirm.id)}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                    data-testid="confirm-delete-order-btn"
                  >
                    Delete Permanently
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
          </>
        )}
      </div>
    </div>
  );
};
